import { captureError, captureMessage } from '../utils/sentry';
import { useToastStore } from '../hooks/useToast';

export interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  sessionId?: string;
  url?: string;
  userAgent?: string;
  timestamp?: Date;
  [key: string]: any;
}

export interface NetworkError extends Error {
  status?: number;
  code?: string;
  isNetworkError: boolean;
}

export class ErrorService {
  private static instance: ErrorService;

  static getInstance(): ErrorService {
    if (!ErrorService.instance) {
      ErrorService.instance = new ErrorService();
    }
    return ErrorService.instance;
  }

  // Handle different types of errors
  handleError(
    error: Error | NetworkError,
    context?: ErrorContext,
    showToast: boolean = true
  ) {
    const errorContext = {
      ...context,
      timestamp: new Date(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    // Log error locally
    console.error('Error handled by ErrorService:', error, errorContext);

    // Determine error type and handle accordingly
    if (this.isNetworkError(error)) {
      this.handleNetworkError(error as NetworkError, errorContext, showToast);
    } else if (this.isValidationError(error)) {
      this.handleValidationError(error, errorContext, showToast);
    } else if (this.isAuthError(error)) {
      this.handleAuthError(error, errorContext, showToast);
    } else {
      this.handleGenericError(error, errorContext, showToast);
    }

    // Send to monitoring service
    captureError(error, errorContext);
  }

  private handleNetworkError(
    error: NetworkError,
    _context: ErrorContext,
    showToast: boolean
  ) {
    let message = 'Network error occurred';
    let title = 'Connection Problem';

    if (!navigator.onLine) {
      title = "You're Offline";
      message = 'Please check your internet connection and try again.';
    } else if (error.status) {
      switch (error.status) {
        case 408:
          title = 'Request Timeout';
          message = 'The request took too long. Please try again.';
          break;
        case 429:
          title = 'Too Many Requests';
          message = 'Please wait a moment before trying again.';
          break;
        case 500:
        case 502:
        case 503:
        case 504:
          title = 'Server Error';
          message =
            'Our servers are experiencing issues. Please try again later.';
          break;
        default:
          message = `Network error (${error.status}). Please try again.`;
      }
    }

    if (showToast) {
      useToastStore.getState().addToast({
        type: 'error',
        title,
        message,
        duration: 5000,
      });
    }
  }

  private handleValidationError(
    error: Error,
    _context: ErrorContext,
    showToast: boolean
  ) {
    if (showToast) {
      useToastStore.getState().addToast({
        type: 'warning',
        title: 'Validation Error',
        message: error.message,
        duration: 4000,
      });
    }
  }

  private handleAuthError(
    _error: Error,
    _context: ErrorContext,
    showToast: boolean
  ) {
    if (showToast) {
      useToastStore.getState().addToast({
        type: 'error',
        title: 'Authentication Error',
        message: 'Please log in again to continue.',
        duration: 5000,
      });
    }

    // Redirect to login or trigger auth flow
    // This could be handled by the auth service
  }

  private handleGenericError(
    error: Error,
    _context: ErrorContext,
    showToast: boolean
  ) {
    const isDevelopment = import.meta.env.MODE === 'development';

    if (showToast) {
      useToastStore.getState().addToast({
        type: 'error',
        title: 'Something went wrong',
        message: isDevelopment
          ? error.message
          : 'An unexpected error occurred. Please try again.',
        duration: 5000,
      });
    }
  }

  // Error type detection
  private isNetworkError(error: Error): boolean {
    return (
      error.message.includes('fetch') ||
      error.message.includes('NetworkError') ||
      error.message.includes('Failed to fetch') ||
      error.message.includes('ERR_NETWORK') ||
      (error as NetworkError).isNetworkError === true
    );
  }

  private isValidationError(error: Error): boolean {
    return (
      error.message.includes('validation') ||
      error.message.includes('invalid') ||
      error.message.includes('required') ||
      error.name === 'ValidationError'
    );
  }

  private isAuthError(error: Error): boolean {
    return (
      error.message.includes('unauthorized') ||
      error.message.includes('authentication') ||
      error.message.includes('token') ||
      error.message.includes('login')
    );
  }

  // Graceful degradation helpers
  handleOfflineMode() {
    captureMessage('User went offline', 'info');
    useToastStore.getState().addToast({
      type: 'warning',
      title: "You're Offline",
      message: 'Some features may not be available.',
      duration: 0,
    });
  }

  handleOnlineMode() {
    captureMessage('User came back online', 'info');
    useToastStore.getState().addToast({
      type: 'success',
      title: "You're Back Online",
      message: 'All features are now available.',
      duration: 3000,
    });
  }

  // API error handling
  handleApiError(response: Response, data?: any): NetworkError {
    const error = new Error(
      data?.message || `HTTP ${response.status}: ${response.statusText}`
    ) as NetworkError;
    error.status = response.status;
    error.isNetworkError = true;

    if (data?.code) {
      error.code = data.code;
    }

    return error;
  }

  // Form error handling
  handleFormErrors(
    errors: Array<{ field: string; message: string }>,
    showToast: boolean = true
  ) {
    if (showToast && errors.length > 0) {
      const firstError = errors[0];
      useToastStore.getState().addToast({
        type: 'error',
        title: 'Form Error',
        message: firstError.message,
        duration: 4000,
      });
    }

    // Log form errors for analytics
    captureMessage('Form validation errors', 'warning', { errors });
  }

  // Retry mechanism
  async withRetry<T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    delay: number = 1000,
    context?: ErrorContext
  ): Promise<T> {
    let lastError: Error;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;

        if (attempt === maxRetries) {
          this.handleError(lastError, { ...context, attempt, maxRetries });
          throw lastError;
        }

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }

    throw lastError!;
  }

  // Performance monitoring
  measurePerformance<T>(operation: () => T, operationName: string): T {
    const startTime = performance.now();

    try {
      const result = operation();
      const duration = performance.now() - startTime;

      if (duration > 1000) {
        // Log slow operations
        captureMessage(`Slow operation: ${operationName}`, 'warning', {
          duration,
        });
      }

      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      this.handleError(error as Error, { operationName, duration });
      throw error;
    }
  }

  async measureAsyncPerformance<T>(
    operation: () => Promise<T>,
    operationName: string
  ): Promise<T> {
    const startTime = performance.now();

    try {
      const result = await operation();
      const duration = performance.now() - startTime;

      if (duration > 2000) {
        // Log slow async operations
        captureMessage(`Slow async operation: ${operationName}`, 'warning', {
          duration,
        });
      }

      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      this.handleError(error as Error, { operationName, duration });
      throw error;
    }
  }
}

// Singleton instance
export const errorService = ErrorService.getInstance();

// Utility functions
export const handleError = (
  error: Error,
  context?: ErrorContext,
  showToast?: boolean
) => {
  errorService.handleError(error, context, showToast);
};

export const withRetry = <T>(
  operation: () => Promise<T>,
  maxRetries?: number,
  delay?: number,
  context?: ErrorContext
) => {
  return errorService.withRetry(operation, maxRetries, delay, context);
};

export const measurePerformance = <T>(
  operation: () => T,
  operationName: string
) => {
  return errorService.measurePerformance(operation, operationName);
};

export const measureAsyncPerformance = <T>(
  operation: () => Promise<T>,
  operationName: string
) => {
  return errorService.measureAsyncPerformance(operation, operationName);
};
